/**
 * Score Display Component
 * Trust/Maturity/Compliance score displays with gauges
 */

import React from 'react';
import { Progress, Tooltip } from 'antd';
import { QuestionCircleOutlined } from '@ant-design/icons';
import { getTrustScoreColor, getTrustScoreStatus } from '../../theme/componentVariants';
import { typography, spacing } from '../../theme/designTokens';

/**
 * Score Display Component
 *
 * @param {number} score - Score value (0-100)
 * @param {string} type - Display type: 'gauge' | 'progress' | 'text'
 * @param {string} size - Size: 'small' | 'medium' | 'large'
 * @param {string} label - Label text
 * @param {string} tooltip - Tooltip text
 * @param {boolean} showValue - Show numeric value (default: true)
 * @param {object} style - Additional styles
 */
function ScoreDisplay({
  score,
  type = 'gauge',
  size = 'medium',
  label,
  tooltip,
  showValue = true,
  style = {},
}) {
  if (score === null || score === undefined) {
    return <span style={{ color: '#888' }}>N/A</span>;
  }

  const scoreValue = Math.round(score);
  const scoreColor = getTrustScoreColor(scoreValue);
  const scoreStatus = getTrustScoreStatus(scoreValue);

  // Size configurations
  const sizeConfig = {
    small: { width: 80, strokeWidth: 6, fontSize: 12 },
    medium: { width: 120, strokeWidth: 8, fontSize: 14 },
    large: { width: 160, strokeWidth: 10, fontSize: 16 },
  };

  const config = sizeConfig[size] || sizeConfig.medium;

  // Gauge display
  if (type === 'gauge') {
    return (
      <div style={{ textAlign: 'center', ...style }}>
        <Progress
          type="circle"
          percent={scoreValue}
          width={config.width}
          strokeWidth={config.strokeWidth}
          strokeColor={scoreColor}
          format={(percent) => (
            <span style={{ fontSize: config.fontSize, fontWeight: typography.fontWeight.bold }}>
              {percent}
            </span>
          )}
        />
        {label && (
          <div style={{ marginTop: spacing.xs, fontSize: typography.fontSize.sm }}>
            {label}
            {tooltip && (
              <Tooltip title={tooltip}>
                <QuestionCircleOutlined style={{ marginLeft: spacing.xs, color: '#888' }} />
              </Tooltip>
            )}
          </div>
        )}
      </div>
    );
  }

  // Progress bar display
  if (type === 'progress') {
    return (
      <div style={style}>
        {label && (
          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: spacing.xxs }}>
            <span style={{ fontSize: typography.fontSize.sm }}>
              {label}
              {tooltip && (
                <Tooltip title={tooltip}>
                  <QuestionCircleOutlined style={{ marginLeft: spacing.xs, color: '#888' }} />
                </Tooltip>
              )}
            </span>
            {showValue && (
              <span style={{ fontSize: typography.fontSize.sm, fontWeight: typography.fontWeight.semibold }}>
                {scoreValue}%
              </span>
            )}
          </div>
        )}
        <Progress
          percent={scoreValue}
          strokeColor={scoreColor}
          showInfo={!label && showValue}
          strokeWidth={config.strokeWidth}
        />
      </div>
    );
  }

  // Text display
  if (type === 'text') {
    return (
      <span
        style={{
          fontSize: config.fontSize,
          fontWeight: typography.fontWeight.bold,
          color: scoreColor,
          ...style,
        }}
      >
        {scoreValue}
        {label && <span style={{ fontSize: typography.fontSize.xs, marginLeft: spacing.xs }}>{label}</span>}
        {tooltip && (
          <Tooltip title={tooltip}>
            <QuestionCircleOutlined style={{ marginLeft: spacing.xs, color: '#888', fontSize: typography.fontSize.xs }} />
          </Tooltip>
        )}
      </span>
    );
  }

  return null;
}

export default ScoreDisplay;
